import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Crown, Sparkles, Coins, Receipt, CalendarClock, AlertTriangle,
  CheckCircle2, XCircle, Loader2, ChevronRight, ArrowUpRight,
} from "lucide-react";

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function StatusBadge({ status, payment_status, lang }) {
  const paid = payment_status === "paid";
  const expired = status === "expired" || (payment_status === "unpaid" && status === "complete");
  if (paid) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-bold">
      <CheckCircle2 className="w-3 h-3" /> {lang === "fr" ? "Payé" : "Paid"}
    </span>
  );
  if (expired) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-bold">
      <XCircle className="w-3 h-3" /> {lang === "fr" ? "Échec" : "Failed"}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold">
      <Loader2 className="w-3 h-3 animate-spin" /> {lang === "fr" ? "En attente" : "Pending"}
    </span>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/login?next=/billing"); return; }
    (async () => {
      try {
        const [{ data: b }, { data: t }] = await Promise.all([
          api.get("/me/billing"),
          api.get("/stripe/my-transactions"),
        ]);
        setBilling(b);
        setTxns(t?.transactions || []);
      } catch (e) {
        toast.error(lang === "fr" ? "Impossible de charger vos données" : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]); // eslint-disable-line

  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/stripe/cancel-subscription");
      toast.success(data?.message || (lang === "fr" ? "Abonnement annulé" : "Subscription cancelled"));
      const { data: b } = await api.get("/me/billing");
      setBilling(b);
      setConfirmCancel(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec de l'annulation" : "Cancel failed"));
    } finally {
      setCancelling(false);
    }
  };

  if (!user) return null;

  const planTone = billing?.plan === "pro" ? "navy" : billing?.plan === "amateur" ? "pink" : "free";
  const PlanIcon = billing?.plan === "pro" ? Crown : billing?.plan === "amateur" ? Sparkles : Coins;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="billing-portal">
      <span className="chip-pink">{lang === "fr" ? "Espace facturation" : "Billing portal"}</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
        {lang === "fr" ? "Vos abonnements & crédits" : "Your subscriptions & credits"}
      </h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {lang === "fr"
          ? "Gérez votre plan, suivez vos paiements et téléchargez vos reçus. Paiement sécurisé par Stripe."
          : "Manage your plan, track payments and download receipts. Secure payments by Stripe."}
      </p>

      {loading && (
        <div className="mt-12 flex items-center gap-3 text-[#8B95A1]">
          <Loader2 className="w-5 h-5 animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}
        </div>
      )}

      {!loading && billing && (
        <>
          {/* Current plan + credits */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div
              data-testid="billing-plan-card"
              className={`rounded-3xl border-2 p-7 ${
                planTone === "navy" ? "border-[#1F3A5F] bg-[#1F3A5F] text-white" :
                planTone === "pink" ? "border-[#E5276A] bg-white" :
                "border-[#1F3A5F]/12 bg-[#FBF7F3]"
              }`}
            >
              <div className="flex items-center gap-2">
                <PlanIcon className={`w-6 h-6 ${planTone === "navy" ? "text-[#FFD8E5]" : "text-[#E5276A]"}`} />
                <div className={`text-[11px] uppercase tracking-widest font-bold ${planTone === "navy" ? "text-[#FFD8E5]" : "text-[#E5276A]"}`}>
                  {lang === "fr" ? "Plan actuel" : "Current plan"}
                </div>
              </div>
              <div className={`font-display text-3xl mt-2 ${planTone === "navy" ? "text-white" : "text-[#1F3A5F]"}`}>
                {billing.plan_label || (lang === "fr" ? "Découverte" : "Free")}
              </div>
              <div className={`mt-1 text-sm ${planTone === "navy" ? "text-white/80" : "text-[#4A5568]"}`}>
                {billing.subscription_active
                  ? (lang === "fr" ? `Renouvellement le ${formatDate(billing.subscription_period_end, lang)}` : `Renews on ${formatDate(billing.subscription_period_end, lang)}`)
                  : billing.cancelled_at
                    ? (lang === "fr" ? `Annulé · accès jusqu'au ${formatDate(billing.subscription_period_end, lang)}` : `Cancelled · access until ${formatDate(billing.subscription_period_end, lang)}`)
                    : (lang === "fr" ? "Aucun abonnement actif" : "No active subscription")}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {billing.plan !== "pro" && (
                  <Link
                    to="/pricing"
                    data-testid="billing-upgrade-btn"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold ${
                      planTone === "navy"
                        ? "bg-white text-[#1F3A5F] hover:bg-[#FFD8E5]"
                        : "bg-[#E5276A] text-white hover:bg-[#C01661]"
                    }`}
                  >
                    {billing.subscription_active
                      ? (lang === "fr" ? "Changer de plan" : "Change plan")
                      : (lang === "fr" ? "Choisir un plan" : "Choose a plan")}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
                {/* Renew now — only when user has a plan but auto_renew is OFF (cancelled) and still in grace period */}
                {billing.subscription_active && billing.cancelled_at && (
                  <button
                    data-testid="billing-renew-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/stripe/renewal-link", {});
                        if (data?.url) window.location.href = data.url;
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Échec");
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold ${
                      planTone === "navy" ? "bg-[#E5276A] text-white hover:bg-[#C01661]" : "bg-[#1F3A5F] text-white hover:bg-[#2A4870]"
                    }`}
                  >
                    {lang === "fr" ? "Réactiver auto-renouvellement" : "Reactivate auto-renew"}
                  </button>
                )}
                {billing.subscription_active && !billing.cancelled_at && (
                  <button
                    data-testid="billing-cancel-btn"
                    onClick={() => setConfirmCancel(true)}
                    className={`text-xs underline ${planTone === "navy" ? "text-white/70 hover:text-white" : "text-[#8B95A1] hover:text-[#1F3A5F]"}`}
                  >
                    {lang === "fr" ? "Résilier" : "Cancel subscription"}
                  </button>
                )}
              </div>
            </div>

            <div data-testid="billing-credits-card" className="rounded-3xl border-2 border-[#1F3A5F]/12 bg-white p-7">
              <div className="flex items-center gap-2">
                <Coins className="w-6 h-6 text-[#E5276A]" />
                <div className="text-[11px] uppercase tracking-widest font-bold text-[#E5276A]">
                  {lang === "fr" ? "Crédits disponibles" : "Available credits"}
                </div>
              </div>
              <div className="font-display text-5xl text-[#1F3A5F] mt-2" data-testid="billing-credits-value">
                {billing.credits ?? 0}
              </div>
              <div className="mt-1 text-sm text-[#4A5568]">
                {lang === "fr"
                  ? "Utilisables sur les actions IA premium (build, identifier, story, parcours…)."
                  : "Usable across premium AI actions (build, identify, story, routes…)."}
              </div>
              <Link
                to="/pricing#credit-packs"
                data-testid="billing-buy-credits-btn"
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]"
              >
                {lang === "fr" ? "Acheter des crédits" : "Buy credits"} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Transactions */}
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-[#E5276A]" />
              <h2 className="font-display text-2xl text-[#1F3A5F]">
                {lang === "fr" ? "Historique de paiements" : "Payment history"}
              </h2>
            </div>

            {txns.length === 0 ? (
              <div className="app-card p-8 text-center text-sm text-[#8B95A1]" data-testid="billing-no-txns">
                {lang === "fr" ? "Aucun paiement pour le moment." : "No payments yet."}
              </div>
            ) : (
              <div className="app-card divide-y divide-[#1F3A5F]/8" data-testid="billing-txns-list">
                {txns.map((t) => (
                  <div
                    key={t.session_id}
                    data-testid={`billing-txn-${t.session_id}`}
                    className="p-4 md:p-5 flex flex-wrap gap-3 items-center justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[#1F3A5F] text-sm">
                          {t.metadata?.package_id?.startsWith("sub_")
                            ? (t.metadata?.plan === "pro" ? "Pro · 1 mois" : "Amateur · 1 mois")
                            : t.kind === "credits" ? `Pack ${t.metadata?.credits_grant || "?"} crédits` : t.package_id}
                        </span>
                        <StatusBadge status={t.status} payment_status={t.payment_status} lang={lang} />
                        {t.plan_granted && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] text-[10px] font-bold">
                            <Crown className="w-3 h-3" /> {lang === "fr" ? "Plan activé" : "Plan active"}
                          </span>
                        )}
                        {t.credits_granted && t.kind === "credits" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] text-[10px] font-bold">
                            <Coins className="w-3 h-3" /> +{t.metadata?.credits_grant} {lang === "fr" ? "crédités" : "credited"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#8B95A1] mt-1 flex items-center gap-1.5">
                        <CalendarClock className="w-3 h-3" /> {formatDate(t.created_at, lang)}
                        <span className="text-[#1F3A5F]/30">·</span>
                        <span className="font-mono">{t.session_id.slice(-10)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl text-[#1F3A5F]">
                        {(t.amount_eur ?? 0).toFixed(2)} €
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Cancel confirmation modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 bg-[#1F3A5F]/60 flex items-end md:items-center justify-center md:p-5" data-testid="billing-cancel-modal">
          <div className="bg-white w-full max-w-md shadow-2xl flex flex-col rounded-t-3xl md:rounded-3xl max-h-[92dvh]">
            <div className="overflow-y-auto p-6 md:p-7">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-[#1F3A5F]">
                  {lang === "fr" ? "Résilier votre abonnement ?" : "Cancel subscription?"}
                </h3>
                <p className="text-sm text-[#4A5568] mt-2">
                  {lang === "fr"
                    ? "Vous conservez l'accès à vos avantages premium jusqu'à la fin de la période en cours. Aucun remboursement n'est effectué."
                    : "You'll keep premium access until the end of your current period. No refund is issued."}
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setConfirmCancel(false)}
                disabled={cancelling}
                className="px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:bg-[#FBF7F3]"
                data-testid="billing-cancel-abort"
              >
                {lang === "fr" ? "Garder l'abonnement" : "Keep subscription"}
              </button>
              <button
                onClick={cancel}
                disabled={cancelling}
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 inline-flex items-center gap-2"
                data-testid="billing-cancel-confirm"
              >
                {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                {lang === "fr" ? "Résilier" : "Cancel"}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-12 text-center text-xs text-[#8B95A1]">
        <a href="https://stripe.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#1F3A5F]">
          {lang === "fr" ? "Paiements sécurisés par Stripe" : "Secure payments by Stripe"} <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
